import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

/**
 * Formats date string to readable format (e.g., "01 Jan 2024")
 */
const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

/**
 * Formats amount to INR currency format
 */
const formatAmount = (amount) => {
    if (!amount) return '₹0.00';
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 2
    }).format(amount);
};

/**
 * Gets status text from status code
 */
const getStatusText = (status) => {
    const statusMap = {
        11: 'No BRC Updated',
        12: 'BRC Partially Updated',
        13: 'BRC Updated',
    };
    return statusMap[status] || 'Unknown Status';
};

/**
 * Gets BRC type label
 */
const getBrcTypeLabel = (manualBrc) => {
    if (manualBrc === null) return 'All';
    if (manualBrc === 1) return 'Manual';
    if (manualBrc === 0) return 'Auto';
    return 'All';
};

/**
 * Exports scrip report data to Excel with styling
 * @param {Object} params - Export parameters
 * @param {Array} params.data - Array of scrip data to export
 * @param {Array} params.selectedScrips - Array of selected scrip IDs (if any)
 * @param {string} params.searchQuery - Search query filter
 * @param {string} params.startDate - Start date filter
 * @param {string} params.endDate - End date filter
 * @param {number|null} params.manualBrc - BRC type filter (null=All, 1=Manual, 0=Auto)
 * @param {number} params.currentPage - Current page number
 * @param {number} params.perPage - Items per page
 */
export const exportScripReportToExcel = async ({
    data,
    selectedScrips = [],
    searchQuery = '',
    startDate = '',
    endDate = '',
    manualBrc = null,
    currentPage = 1,
    perPage = 25
}) => {
    // Determine which data to export
    const dataToExport = selectedScrips.length > 0
        ? data.filter(scrip => selectedScrips.includes(scrip.id))
        : data;

    if (dataToExport.length === 0) {
        return { success: false, message: 'No data to export' };
    }

    // Create workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Scrip Report');

    // Define styles
    const yellowFill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFB8D4E8' } // Yellow
    };

    const lightBlueFill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFB8D4E8' } // Light Blue
    };

    const headerFont = {
        bold: true,
        size: 11
    };

    const filterFont = {
        bold: true,
        size: 10
    };

    // Set column widths
    worksheet.columns = [
        { width: 8 },   // S.No
        { width: 25 },  // Client Name
        { width: 12 },  // Port Code
        { width: 15 },  // Scrip No.
        { width: 15 },  // Scrip Date
        { width: 18 },  // Scrip Amount
        { width: 12 },  // Type
        { width: 18 }   // Status
    ];

    let currentRow = 1;

    // Add filter rows with yellow background
    const addFilterRow = (text) => {
        const row = worksheet.getRow(currentRow);
        row.getCell(1).value = text;
        row.getCell(1).fill = yellowFill;
        row.getCell(1).font = filterFont;
        // Merge cells for filter row to span across columns
        worksheet.mergeCells(currentRow, 1, currentRow, 8);
        currentRow++;
    };

    // Add date range if either date is provided
    if (startDate || endDate) {
        const startFormatted = startDate ? formatDate(startDate) : 'N/A';
        const endFormatted = endDate ? formatDate(endDate) : 'N/A';
        addFilterRow(`Date Range: ${startFormatted} - ${endFormatted}`);
    }

    // Add BRC Type
    // addFilterRow(`BRC Type: ${getBrcTypeLabel(manualBrc)}`);

    // Add search query if provided
    // if (searchQuery && searchQuery.trim()) {
    //     addFilterRow(`Search: ${searchQuery.trim()}`);
    // }

    // Add empty row after filters
    currentRow++;

    // Add headers with light blue background
    const headers = [
        'S.No',
        'Client Name',
        'Port Code',
        'Scrip No.',
        'Scrip Date',
        'Scrip Amount',
        'Type',
        'Status'
    ];

    const headerRow = worksheet.getRow(currentRow);
    headers.forEach((header, index) => {
        const cell = headerRow.getCell(index + 1);
        cell.value = header;
        cell.fill = lightBlueFill;
        cell.font = headerFont;
        cell.alignment = { horizontal: 'left', vertical: 'middle' };
        cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
        };
    });
    currentRow++;

    // Add data rows
    dataToExport.forEach((scrip, index) => {
        const serialNo = selectedScrips.length > 0
            ? index + 1
            : (currentPage - 1) * perPage + index + 1;

        const row = worksheet.getRow(currentRow);
        const rowData = [
            serialNo,
            scrip.client_name || 'N/A',
            scrip.port_code || 'N/A',
            scrip.scrip_no || 'N/A',
            formatDate(scrip.scrip_date),
            formatAmount(scrip.scrip_amount),
            scrip.manual_brc ? 'Manual' : 'Automation',
            getStatusText(scrip.status)
        ];

        rowData.forEach((value, colIndex) => {
            const cell = row.getCell(colIndex + 1);
            cell.value = value;
            cell.alignment = { horizontal: 'left', vertical: 'middle' };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });
        currentRow++;
    });

    // Add empty row before total
    currentRow++;

    // Calculate total amount
    const totalAmount = dataToExport.reduce(
        (sum, scrip) => sum + (parseFloat(scrip.scrip_amount) || 0),
        0
    );

    // Add total row with yellow background
    const totalRow = worksheet.getRow(currentRow);
    
    // Total Amount label
    const labelCell = totalRow.getCell(5);
    labelCell.value = 'Total Amount:';
    labelCell.fill = yellowFill;
    labelCell.font = { bold: true, size: 11 };
    labelCell.alignment = { horizontal: 'right', vertical: 'middle' };
    labelCell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
    };

    // Total Amount value
    const valueCell = totalRow.getCell(6);
    valueCell.value = formatAmount(totalAmount);
    valueCell.fill = yellowFill;
    valueCell.font = { bold: true, size: 11 };
    valueCell.alignment = { horizontal: 'left', vertical: 'middle' };
    valueCell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
    };

    // Generate and download file
    try {
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { 
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        saveAs(blob, 'scrip_report.xlsx');
        return { success: true, message: 'Excel downloaded successfully' };
    } catch (error) {
        console.error('Excel generation error:', error);
        return { success: false, message: 'Failed to generate Excel file' };
    }
};